/**
 * SEO.tsx
 * Reusable per-route head tags (title, description, canonical, OG).
 * Uses react-helmet (already a project dependency).
 */
import { Helmet } from "react-helmet";

interface SEOProps {
  title: string;
  description: string;
  path: string; // e.g. "/gridstable"
  ogType?: "website" | "article";
  image?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Breadcrumb trail after "Startseite", e.g. [{ name: "Energieglück+", path: "/plattform" }] */
  breadcrumbs?: { name: string; path: string }[];
}

const SITE = "https://energieglueck.de";

const SEO = ({ title, description, path, ogType = "website", image, jsonLd, breadcrumbs }: SEOProps) => {
  const url = path === "/" ? `${SITE}/` : `${SITE}${path}`;
  const og = image || `${SITE}/og-image.jpg`;
  const ld = jsonLd ? (Array.isArray(jsonLd) ? [...jsonLd] : [jsonLd]) : [];
  if (breadcrumbs?.length) {
    const trail = [{ name: "Startseite", path: "/" }, ...breadcrumbs];
    ld.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: trail.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: b.path === "/" ? `${SITE}/` : `${SITE}${b.path}`,
      })),
    });
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={og} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={og} />
      {ld.map((item, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
